"use client";
import React, { Fragment, useState } from "react";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import Image from "next/image";
import {
  ArrowRight, Play,
  Layers, CheckCircle2, X
} from "lucide-react";
import { TOTAL_FEATURES } from "./featureData";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { useLanguage } from "@/contexts/LanguageContext";

/* Headline reveal: line one lands word by word, then the gradient payoff line
   rises as a single unit — animating its words individually would give each one
   its own teal gradient sweep instead of one continuous ramp. */
const EASE_OUT = [0.22, 1, 0.36, 1] as [number, number, number, number];

const headlineStack: Variants = {
  hidden: {},
  show: { transition: { delayChildren: 0.1, staggerChildren: 0.45 } },
};

const wordStack: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const riseIn: Variants = {
  hidden: { opacity: 0, y: 22, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: EASE_OUT },
  },
};

/* Tile accent colours. Index-aligned with `features.hero.cardTiles` in the
   dictionary — the labels move with the language, the palette doesn't. */
const CARD_TILE_COLORS = [
  "#0284C7", "#DC2626", "#65A30D", "#D97706",
  "#3D5EE1", "#2563EB", "#B45309", "#4D7C0F",
  "#9333EA", "#4338CA", "#C05621", "#B91C1C",
];

export default function FeaturesHero() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const { t, tv, tf } = useLanguage();

  const headlineLead = tv<string[]>("features.hero.headlineLead");
  const cardTiles = tv<string[]>("features.hero.cardTiles");

  /* The overlay is fixed, so without this the wheel keeps reaching the page
     behind it and the hero scrolls away under the open video. */
  useBodyScrollLock(isVideoOpen);

  return (
    <section
      className="relative w-full min-h-screen lg:min-h-[100svh] lg:h-[100svh]
                 overflow-hidden font-sans
                 flex items-center pt-20 sm:pt-24 lg:pt-[76px] pb-8 lg:pb-6"
    >
      {/* Hero background — an <Image>, not a CSS `background-image`, for the
          reason spelled out in Hero.tsx: a CSS url() bypasses the
          /_next/image optimizer, so this 1.35MB PNG was being served whole to
          every phone. Routed through next/image it is re-encoded and cut to
          the device width. `fill` + object-cover reproduces the old
          `bg-cover bg-center`. */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Image
          src="/blog-banners/feature hero-section- bg.png"
          alt=""
          aria-hidden
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-white/10 via-transparent to-white/20 pointer-events-none" />

      <div
        className="site-container relative z-10 flex flex-col lg:flex-row
                    items-center justify-between gap-8 lg:gap-6 xl:gap-4
                    w-full min-h-fit"
      >

        {/* ─── LEFT COLUMN ─── */}
        <div className="w-full lg:w-[45%] flex flex-col flex-shrink-0 justify-center">

          {/* Headline */}
          <motion.h1
            variants={headlineStack}
            initial="hidden"
            animate="show"
            /* `lg` steps *down* from `sm`: at 1024px the headline stops being
               full-bleed and moves into a ~420px column, so the 52px cut it used
               to take there would wrap to four lines. */
            className="font-heading font-light text-[30px] min-[380px]:text-[34px] sm:text-[44px] lg:text-[38px] xl:text-[46px] 2xl:text-[56px] leading-[1.16] text-[#0F172A] tracking-[-0.035em] mb-3 sm:mb-4"
          >
            <motion.span variants={wordStack} className="block">
              {/* Keyed by position, not by the word: Hindi and English share a
                  word count but not the words, so a text key would remount
                  every span on a language switch and replay the stagger. */}
              {headlineLead.map((w, i) => (
                <Fragment key={i}>
                  {/* The gap between words is a 0.26em margin, not whitespace,
                      so the accessibility tree and a copy-paste both saw one
                      run-on token. English survived that on camelCase alone
                      ("RunYourEntireSchool"); Devanagari has no case, so
                      "एकहीस्क्रीनसे" is simply unreadable. An sr-only space
                      restores the word boundary and renders nothing. */}
                  {i > 0 && <span className="sr-only"> </span>}
                  <motion.span variants={riseIn} className="inline-block mr-[0.26em] will-change-transform">
                    {w}
                  </motion.span>
                </Fragment>
              ))}
            </motion.span>
            <motion.span
              variants={riseIn}
              className="block font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#1E3BB3] via-[#3D5EE1] to-[#60A5FA] will-change-transform"
            >
              {t("features.hero.headlineAccent")}
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.85 }}
            className="text-[13px] sm:text-[15px] text-slate-500 max-w-[460px] leading-[1.6] mb-5 sm:mb-6"
          >
            {t("features.hero.subtitle")}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.95 }}
            className="flex flex-wrap items-center gap-3"
          >
            <a
              href="/contact"
              className="bg-[#3D5EE1] hover:bg-[#2A48C9] text-white px-6 sm:px-7 py-2.5 sm:py-3 rounded-full font-semibold
                         transition-all duration-300 hover:-translate-y-0.5
                         shadow-[0_8px_24px_-6px_rgba(61,94,225,0.45)]
                         flex items-center gap-2 text-[13.5px] sm:text-[15px] group"
            >
              {t("features.hero.ctaDemo")}
              <ArrowRight size={14} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <button
              onClick={() => setIsVideoOpen(true)}
              className="bg-white/80 hover:bg-white text-slate-800 border border-slate-200
                         px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold
                         transition-all duration-300 hover:-translate-y-0.5
                         flex items-center gap-2.5 text-[13.5px] sm:text-[15px] shadow-sm group backdrop-blur-sm cursor-pointer"
            >
              <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors shrink-0">
                <Play size={8} className="text-slate-700 fill-current ml-0.5" />
              </span>
              {t("features.hero.ctaWatch")}
            </button>
          </motion.div>

        </div>{/* end left column */}

        {/* ─── RIGHT COLUMN ─── */}
        <div className="w-full lg:w-[53%] flex items-center justify-center flex-shrink-0 mt-6 lg:mt-0">

          <div className="relative w-full max-w-[660px] xl:max-w-[780px] 2xl:max-w-[860px]">
            {/* Main Feature Suite Card
                Entrance runs once in Framer; the endless float is a CSS keyframe on an
                outer wrapper so it stays on the compositor instead of re-rendering the
                card (and its large blurred shadow) from JS on every frame. */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              style={{ willChange: "transform, opacity" }}
            >
            {/* The 16/10 lock only applies from `sm` up. On a phone the card is
                narrow enough that 16/10 makes it shorter than its own 2-column
                feature grid, and `overflow-hidden` was silently slicing 6 of the
                12 features in half. Below `sm` the height follows the content. */}
            <div
              className="lp-hero-float relative w-full sm:aspect-[16/10] bg-white rounded-[20px] sm:rounded-[26px]
                         shadow-[0_24px_70px_-10px_rgba(61,94,225,0.18)]
                         border border-slate-200/70 overflow-hidden flex flex-col z-10"
            >
              {/* Header Row */}
              <div className="h-10 sm:h-12 xl:h-14 border-b border-slate-100 flex items-center justify-between px-4 sm:px-5 xl:px-6 bg-white shrink-0">
                <div className="flex items-center gap-2 xl:gap-2.5">
                  <div className="w-4 h-4 xl:w-5 xl:h-5 rounded-md bg-[#3D5EE1] flex items-center justify-center">
                    <Layers size={9} className="text-white xl:w-[11px] xl:h-[11px]" />
                  </div>
                  <span className="text-[11px] sm:text-[13px] xl:text-[15px] font-bold text-slate-800">{t("features.hero.cardTitle")}</span>
                </div>
                <div className="flex gap-1.5">
                  {["bg-red-400", "bg-yellow-400", "bg-green-400"].map(c => (
                    <div key={c} className={`w-2.5 h-2.5 xl:w-3 xl:h-3 rounded-full ${c}`} />
                  ))}
                </div>
              </div>

              {/* Feature Grid inside card */}
              <div className="p-3 sm:p-5 xl:p-6 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 xl:gap-4 flex-1 overflow-hidden">
                {cardTiles.map((label, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 xl:gap-2.5 bg-slate-50 hover:bg-white rounded-xl xl:rounded-[14px] px-2.5 py-2 xl:px-4 xl:py-2.5 border border-slate-100 hover:border-indigo-200 transition-[background-color,border-color] duration-200 group"
                  >
                    <CheckCircle2 size={13} style={{ color: CARD_TILE_COLORS[i] }} className="shrink-0 xl:w-4 xl:h-4" />
                    <span className="text-[10px] sm:text-[11.5px] xl:text-[13.5px] text-slate-700 leading-tight group-hover:text-[#3D5EE1] transition-colors">{label}</span>
                  </div>
                ))}
              </div>

              {/* Bottom CTA strip */}
              <div className="border-t border-slate-100 px-4 sm:px-5 xl:px-6 py-2.5 sm:py-3 xl:py-4 flex items-center justify-between bg-indigo-50/50 shrink-0">
                <span className="text-[10px] sm:text-[12px] xl:text-[13.5px] text-slate-600 font-medium">{tf("features.hero.cardCount", { count: TOTAL_FEATURES })}</span>
                <div className="flex items-center gap-1 xl:gap-1.5 text-[10px] sm:text-[11px] xl:text-[12.5px] text-[#3D5EE1] font-bold">
                  <Layers size={12} className="xl:w-[14px] xl:h-[14px]" />
                  {t("features.hero.cardAllIncluded")}
                </div>
              </div>
            </div>
            </motion.div>

          </div>

        </div>{/* end right column */}

      </div>

      {/* ── Video Overview Modal ── */}
      <AnimatePresence>
        {isVideoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/80 backdrop-blur-md"
            onClick={() => setIsVideoOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-4xl bg-slate-950 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-slate-800 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Top Bar */}
              <div className="flex items-center justify-between px-5 py-3.5 bg-slate-900 border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-brand-500/20 flex items-center justify-center">
                    <Play size={12} className="text-brand-400 fill-current ml-0.5" />
                  </div>
                  <span className="text-sm font-bold text-white tracking-tight">{t("features.hero.modalTitle")}</span>
                </div>
                <button
                  onClick={() => setIsVideoOpen(false)}
                  aria-label={t("features.hero.modalClose")}
                  className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Video Container */}
              <div className="relative w-full aspect-video bg-black flex items-center justify-center">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube-nocookie.com/embed/5qap5aO4i9A?autoplay=1&rel=0"
                  title={t("features.hero.modalVideoTitle")}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

